import { useId, useState } from 'react';
import { useStorefrontPublicCategoryPickerOptions, type SupabaseClient } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared/i18n';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  PickerOption,
  PickerPrompt,
  PickerSearch,
  PickerSelection,
  PickerStatus,
  type PickerPromptTone,
} from './picker-parts';

export interface CategoryDestinationPickerProps {
  readonly client: SupabaseClient;
  readonly supabaseUrl: string;
  readonly storeId: string;
  /** The stored category id ('' = nothing picked yet). */
  readonly selectedId: string;
  /** Its name from the store's own list (PD10); never offered as an option. */
  readonly selectedLabel: string | null;
  readonly unavailable: boolean;
  /** How the "choose one" prompt reads while nothing is picked. */
  readonly promptTone?: PickerPromptTone;
  readonly onSelect: (categoryId: string) => void;
  readonly testIdPrefix: string;
}

/**
 * A searchable list of the categories a shopper can open, from the f075
 * picker RPC (it works on an unpublished store). Search runs on the server
 * 250 ms after typing stops.
 */
export function CategoryDestinationPicker({
  client,
  supabaseUrl,
  storeId,
  selectedId,
  selectedLabel,
  unavailable,
  promptTone = 'neutral',
  onSelect,
  testIdPrefix,
}: CategoryDestinationPickerProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250);
  const query = useStorefrontPublicCategoryPickerOptions(client, {
    storeId,
    search: debounced,
    supabaseUrl,
  });
  const rows = query.data ?? [];
  const searching = debounced.trim() !== '';
  const label =
    selectedLabel ??
    rows.find((row) => row.id === selectedId)?.name ??
    t('admin.destination.selectedUnknown');

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-category-picker`}>
      {selectedId !== '' ? (
        <PickerSelection
          text={t('admin.destination.selectedCategory', {
            name: label,
            interpolation: { escapeValue: false },
          })}
          unavailable={unavailable}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      <PickerSearch
        id={`${id}-search`}
        label={t('admin.destination.categorySearchLabel')}
        placeholder={t('admin.destination.categorySearchPlaceholder')}
        value={search}
        onChange={setSearch}
        testId={`${testIdPrefix}-category-search`}
      />
      <PickerStatus
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={rows.length === 0}
        loadingKey="admin.destination.loadingCategories"
        retryKey="admin.destination.retryCategories"
        emptyKey={searching ? 'admin.destination.categoryEmpty' : 'admin.destination.noCategories'}
        onRetry={() => void query.refetch()}
        testIdPrefix={`${testIdPrefix}-category`}
      />
      {selectedId === '' && rows.length > 0 && !query.isError && !query.isLoading ? (
        <PickerPrompt
          text={t('admin.destination.pickCategory')}
          tone={promptTone}
          testId={`${testIdPrefix}-category-prompt`}
        />
      ) : null}
      {rows.length > 0 && !query.isError ? (
        <div
          className="border-border divide-border max-h-72 divide-y overflow-y-auto rounded-lg border"
          aria-label={t('admin.destination.pickCategory')}
          role="group"
        >
          {rows.map((category) => (
            <PickerOption
              key={category.id}
              selected={category.id === selectedId}
              onSelect={() => {
                if (category.id !== selectedId) onSelect(category.id);
              }}
              testId={`${testIdPrefix}-category-option-${category.id}`}
            >
              <span className="text-foreground block font-medium">{category.name}</span>
              <span className="text-muted-foreground block text-xs">{category.product_count}</span>
            </PickerOption>
          ))}
        </div>
      ) : null}
    </div>
  );
}
